import type { NotificationWithJoins } from '@dolfin/business/custom_types'
import type {
  NotificationPayoutReference,
  NotificationStatementReference
} from '@dolfin/business/rpc'
import type { IssueWithJoins } from '@dolfin/hooks/useIssues'
import { formatWithLocale } from '@dolfin/utils/dates'
import { startOfMonth } from 'date-fns'

export type NotificationWithReferences = NotificationWithJoins & {
  issue_reference?: IssueWithJoins | null
  payout_reference?: NotificationPayoutReference | null
  statement_reference?: NotificationStatementReference | null
}

type PayoutReferenceLike =
  | NotificationPayoutReference
  | NonNullable<IssueWithJoins['payouts']>

type StatementReferenceLike =
  | NotificationStatementReference
  | NonNullable<IssueWithJoins['statements']>

function uniqueParts(parts: Array<string | null | undefined>) {
  return Array.from(
    new Set(parts.map(part => part?.trim()).filter(Boolean) as string[])
  )
}

function hasLineItems(
  payout: PayoutReferenceLike
): payout is PayoutReferenceLike &
  Pick<NotificationPayoutReference, 'line_items'> {
  return 'line_items' in payout
}

function hasDealLineItems(
  payout: PayoutReferenceLike
): payout is PayoutReferenceLike &
  Pick<NotificationPayoutReference, 'deal_line_items'> {
  return 'deal_line_items' in payout
}

function hasActivities(
  payout: PayoutReferenceLike
): payout is PayoutReferenceLike &
  Pick<NotificationPayoutReference, 'activities'> {
  return 'activities' in payout
}

function hasKpis(
  payout: PayoutReferenceLike
): payout is PayoutReferenceLike & Pick<NotificationPayoutReference, 'kpis'> {
  return 'kpis' in payout
}

function getPayoutSourceLabel(payout: PayoutReferenceLike) {
  const dealItem = hasDealLineItems(payout) ? payout.deal_line_items : null
  if (dealItem) {
    return uniqueParts([
      dealItem.deals?.deal_id ?? dealItem.deals?.name,
      dealItem.product_name ?? dealItem.description ?? dealItem.line_item_id
    ]).join(' · ')
  }
  const dealLabel = payout.deals?.deal_id ?? payout.deals?.name
  if (dealLabel) return dealLabel

  const invoiceLabel =
    payout.invoices?.invoice_number ??
    (hasLineItems(payout)
      ? payout.line_items?.invoices?.invoice_number
      : undefined)
  if (invoiceLabel) return invoiceLabel

  const activityLabel = uniqueParts([
    hasActivities(payout) ? payout.activities?.type : undefined,
    hasActivities(payout) ? payout.activities?.activity_id : undefined
  ]).join(' ')
  if (activityLabel) return activityLabel

  const kpiLabel = hasKpis(payout) ? payout.kpis?.kpis_tables?.label : undefined
  if (kpiLabel) return kpiLabel

  return payout.compensation_rules?.label ?? payout.compensation ?? ''
}

export function formatPayoutReferenceLabel(
  payout?: PayoutReferenceLike | null
) {
  if (!payout) return ''

  return uniqueParts([
    payout.description,
    getPayoutSourceLabel(payout),
    payout.profiles?.name ?? payout.profiles?.email
  ]).join(' · ')
}

export function formatStatementReferenceLabel(
  statement?: StatementReferenceLike | null
) {
  if (!statement) return ''

  const periodLabel = statement.period_start
    ? formatWithLocale(startOfMonth(new Date(statement.period_start)), {
        month: 'short',
        year: 'numeric'
      })
    : ''

  return uniqueParts([
    statement.profiles?.name ?? statement.profiles?.email,
    periodLabel
  ]).join(' · ')
}

export function resolveNotificationPayoutReference(
  notification: NotificationWithReferences
) {
  return (
    notification.payout_reference ??
    notification.issue_reference?.payouts ??
    null
  )
}

export function resolveNotificationStatementReference(
  notification: NotificationWithReferences
) {
  return (
    notification.statement_reference ??
    notification.issue_reference?.statements ??
    null
  )
}

export function formatPayoutReferenceText(payout?: PayoutReferenceLike | null) {
  const label = formatPayoutReferenceLabel(payout)
  return label ? `compensation "${label}"` : 'this compensation'
}

export function formatStatementReferenceText(
  statement?: StatementReferenceLike | null
) {
  const label = formatStatementReferenceLabel(statement)
  return label ? `statement for ${label}` : 'this statement'
}
